/**
 * Schema adapters for the grounding gate's input form (brainstorm-z8t2.24).
 *
 * Pure functions, deliberately kept out of ExecutionInputForm.jsx: that file
 * imports @rjsf/core, which is ESM-only in v6 and cannot be resolved by this
 * project's Jest (CJS) resolver. Webpack resolves it fine at build time, so the
 * component works — but importing it from a test does not. Keeping the logic
 * here means the transforms between roleserver's 422 and a usable form are
 * actually covered by tests.
 *
 * Every shape handled below is one emitted by placeholder_field_for_key() in
 * roleserver/app/services/decomposition_service.py.
 */

/**
 * Narrow the gate's schema to just the outstanding fields.
 *
 * input_form_schema accumulates every execution-instance input ever declared
 * for the workflow, while `missing` is what is still outstanding on THIS
 * attempt. Showing only the missing ones is both the "highlight" the ticket
 * asked for and the honest option: the 422 does not carry current values, so
 * already-satisfied fields could not be pre-filled anyway.
 *
 * Also rewrites `records` fields: the gate emits { type: 'array' } with no
 * `items`, which ajv8 rejects for any input and RJSF renders poorly. A textarea
 * taking JSON (or prose) is parsed back on submit by coerceSubmission.
 *
 * @returns {{schema: object, recordFields: string[]}}
 */
export function buildGateSchema(inputFormSchema, missing) {
  const allProps = (inputFormSchema && inputFormSchema.properties) || {};
  const requested = Array.isArray(missing) ? missing.filter((k) => typeof k === 'string') : [];
  const keys = requested.length ? requested : Object.keys(allProps);

  const properties = {};
  const recordFields = [];

  for (const key of keys) {
    // `missing` is authoritative about what blocks execution. A key the schema
    // somehow lacks must still be collectable, or the user is stuck with no way
    // to proceed.
    const prop = allProps[key] || { type: 'string', 'x-input-kind': 'text' };

    if (prop['x-input-kind'] === 'records' || prop.type === 'array') {
      recordFields.push(key);
      properties[key] = {
        type: 'string',
        format: 'textarea',
        title: prop.title || key,
        description:
          (prop.description ? prop.description + ' ' : '') +
          'Paste a JSON array, or describe the records in plain text.',
      };
    } else {
      properties[key] = { ...prop, title: prop.title || key };
    }
  }

  return {
    schema: { type: 'object', properties, required: keys },
    recordFields,
  };
}

/**
 * Widget hints by JSON Schema `format`, matching TaskInputForm's mapping
 * (buildUiSchema) so the two forms look alike. The gate emits
 * format: 'data-url' for file inputs, which routes to the real uploading
 * FileWidget.
 */
export function buildGateUiSchema(schema) {
  const uiSchema = {};
  for (const [key, prop] of Object.entries((schema && schema.properties) || {})) {
    if (prop.format === 'data-url') uiSchema[key] = { 'ui:widget': 'FileWidget' };
    else if (prop.format === 'textarea') uiSchema[key] = { 'ui:widget': 'textarea' };
    else if (prop.format === 'date') uiSchema[key] = { 'ui:widget': 'date' };
  }
  return uiSchema;
}

/**
 * Turn form data into the request body. `records` fields went out as textareas,
 * so parse them back to arrays when they contain JSON; otherwise send the raw
 * string — the backend treats user input as opaque data, and a description of
 * the records is a legitimate answer.
 */
export function coerceSubmission(formData, recordFields) {
  const out = { ...(formData || {}) };
  for (const key of recordFields || []) {
    const raw = out[key];
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) continue;
    try {
      out[key] = JSON.parse(trimmed);
    } catch {
      // Leave the string as typed — better to send what the user wrote than to
      // block submission on a JSON syntax error in a free-text field.
    }
  }
  return out;
}
